import React, { useMemo, useState } from 'react';
import './Business.css';

import DesktopOnlyBlock from './components/DesktopOnlyBlock';
import CreateBusinessHeader from './components/CreateBusinessHeader';
import LocationSection from './components/LocationSection';
import ScheduleSection from './components/ScheduleSection';
import CreateFooter from './components/CreateFooter';
import MapPickerOverlay from './components/MapPickerOverlay';
import HourPickerModal from './components/HourPickerModal';
import useHttp from '../../hooks/http.hook';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

function BranchSettings({ mode = 'edit' }) {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const { loading, requestWithMeta } = useHttp();
    const [name, setName] = useState('');

    // Schedule format requested: [{ day: 1, open: "09:00", close: "17:00" }, ...]
    const [schedule, setSchedule] = useState(() => {
        return [1, 2, 3, 4, 5, 6, 7].map((day) => ({
            day,
            enabled: day >= 1 && day <= 5, // default Mon–Fri enabled
            open: '09:00',
            close: '17:00',
        }));
    });

    const [lat, setLat] = useState(null);
    const [lon, setLon] = useState(null);
    const [isMapOpen, setIsMapOpen] = useState(false);
    const [timeModal, setTimeModal] = useState({
        open: false,
        day: null,
    });

    const dayLabels = useMemo(
        () => ({
            1: 'Понедельник',
            2: 'Вторник',
            3: 'Среда',
            4: 'Четверг',
            5: 'Пятница',
            6: 'Суббота',
            7: 'Воскресенье',
        }),
        []
    );

    const hourOptions = useMemo(() => {
        return Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}:00`);
    }, []);

    function updateScheduleDay(day, patch) {
        setSchedule((prev) => prev.map((s) => (s.day === day ? { ...s, ...patch } : s)));
    }

    function openTimeModal(day) {
        setTimeModal({ open: true, day });
    }

    function closeTimeModal() {
        setTimeModal({ open: false, day: null });
    }

    const payloadPreview = useMemo(() => {
        const schedulePayload = schedule
            .filter((s) => s.enabled)
            .map(({ day, open, close }) => ({ day, open, close }));

        return {
            name,
            schedule: schedulePayload,
            lat,
            lon,
        };
    }, [name, schedule, lat, lon]);

    const currentScheduleDay = timeModal.day ? schedule.find((x) => x.day === timeModal.day) : null;
    const hourModalTitle =
        timeModal.day
            ? `${dayLabels[timeModal.day]}: время работы`
            : 'Время';

    const isCreateMode = mode === 'create';
    const branchId =
        params.branchId || location?.state?.branch?._id || location?.state?.branch?.id || null;
    const isSaveDisabled =
        loading ||
        name.trim().length === 0 ||
        lat === null ||
        lon === null;

    React.useEffect(() => {
        if (isCreateMode) return;
        if (!branchId) return;

        const applyBranchToState = (b) => {
            if (!b) return;
            setName(b?.name ?? '');
            const loc = b?.location || {};
            if (loc?.lat !== undefined && loc?.lat !== null) setLat(loc.lat);
            if (loc?.lng !== undefined && loc?.lng !== null) setLon(loc.lng);
            if (Array.isArray(b?.schedule)) {
                const byDay = new Map(b.schedule.map((s) => [s.day, s]));
                setSchedule((prev) =>
                    prev.map((d) => {
                        const match = byDay.get(d.day);
                        if (!match) return { ...d, enabled: false };
                        return { ...d, enabled: true, open: match.open, close: match.close };
                    })
                );
            }
        };

        const fromState = location?.state?.branch || null;
        if (fromState) {
            applyBranchToState(fromState);
            return;
        }

        (async () => {
            const res = await requestWithMeta(`/operator/branch/${branchId}`, 'GET');
            if (!res?.ok) return;
            applyBranchToState(res?.data?.branch);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [branchId, isCreateMode]);

    return (
        <div className="shop-create-root">
            <DesktopOnlyBlock />

            <div className="shop-create-mobile">
                <CreateBusinessHeader
                    title={isCreateMode ? 'Создать филиал' : 'Настройки филиала'}
                    subtitle={
                        isCreateMode
                            ? 'Укажите название, локацию и график'
                            : 'Укажите локацию и график'
                    }
                    onBack={() => navigate('/branch/store')}
                />

                <main className="shop-create-content">
                    <div className="shop-form-split shop-form-split--branch">
                        <div className="shop-branch-left">
                            <ScheduleSection
                                className="shop-branch-schedule"
                                schedule={schedule}
                                dayLabels={dayLabels}
                                onToggleDay={(day, enabled) => updateScheduleDay(day, { enabled })}
                                onPickTime={(day) => openTimeModal(day)}
                            />
                        </div>

                        <div className="shop-branch-right">
                            <section className="shop-create-section shop-branch-address">
                                <label className="shop-create-label" htmlFor="branch-name">
                                    Адрес филиала
                                </label>
                                {/* <div className="shop-create-help">Строковое поле: `name`</div> */}
                                <input
                                    id="branch-name"
                                    className="shop-create-input"
                                    placeholder="например, ул. Манаса, 123"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    autoComplete="off"
                                />
                            </section>

                            <LocationSection
                                className="shop-branch-location"
                                lat={lat}
                                lon={lon}
                                onOpenMap={() => setIsMapOpen(true)}
                            />
                        </div>
                    </div>
                </main>

                <CreateFooter
                    disabled={isSaveDisabled}
                    label={
                        loading
                            ? isCreateMode
                                ? 'Создание…'
                                : 'Сохранение…'
                            : isCreateMode
                              ? 'Создать'
                              : 'Сохранить'
                    }
                    onCreate={async () => {
                        // eslint-disable-next-line no-console
                        console.log('Branch payload:', payloadPreview);

                        if (isCreateMode) {
                            const res = await requestWithMeta('/operator/branch', 'POST', payloadPreview);
                            if (res?.ok) {
                                navigate('/branch/store', { replace: true });
                            }
                            return;
                        }

                        if (!branchId) return;
                        const res = await requestWithMeta(`/operator/branch/${branchId}`, 'PUT', payloadPreview);
                        if (res?.ok) {
                            navigate('/branch/store', { replace: true });
                        }
                    }}
                />

                <MapPickerOverlay
                    isOpen={isMapOpen}
                    lat={lat}
                    lon={lon}
                    setLat={setLat}
                    setLon={setLon}
                    onClose={() => setIsMapOpen(false)}
                />

                <HourPickerModal
                    isOpen={timeModal.open}
                    title={hourModalTitle}
                    hourOptions={hourOptions}
                    selectedFrom={currentScheduleDay?.open || '09:00'}
                    selectedTo={currentScheduleDay?.close || '17:00'}
                    onClose={closeTimeModal}
                    onSave={({ open, close }) => {
                        if (!timeModal.day) return;
                        updateScheduleDay(timeModal.day, { open, close });
                        closeTimeModal();
                    }}
                />
            </div>
        </div>
    );
}

export default BranchSettings;

